import React, { useRef, useEffect, useCallback } from 'react'
import { faTimes } from '@fortawesome/pro-light-svg-icons'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { ChatRating } from './ChatRating'
import { ChatWelcomeMessage } from './ChatWelcomeMessage'
import { ChatMessage as ChatMessageType } from '../types'
import { FaIcon } from '../../App/Components/FaIcon/FaIcon'
import '../Chat.css'

type WelcomeMessageProps = {
  label: string
  notMeButtonLabel: string
  showButton: boolean
  onNotMe: () => void
}

type RatingTexts = {
  prompt: string
  commentPlaceholder: string
  submitButton: string
  thankYou: string
}

type ChatPanelProps = {
  messages: ChatMessageType[]
  isLoading: boolean
  onSend: (message: string) => void
  onClose: () => void
  placeholder: string
  closeButtonLabel: string
  logo?: string
  welcomeMessage?: WelcomeMessageProps
  conversationEnded: boolean
  ratingSubmitted: boolean
  onRatingSubmit: (rating: number, comment?: string) => Promise<void>
  ratingTexts: RatingTexts
}

const ThinkingIndicator: React.FC = () => (
  <div className="chat__thinking" data-testid="chat-thinking">
    <span className="chat__thinking-dot" />
    <span className="chat__thinking-dot" />
    <span className="chat__thinking-dot" />
  </div>
)

export const ChatPanel: React.FC<ChatPanelProps> = ({
  messages,
  isLoading,
  onSend,
  onClose,
  placeholder,
  closeButtonLabel,
  logo,
  welcomeMessage,
  conversationEnded,
  ratingSubmitted,
  onRatingSubmit,
  ratingTexts,
}) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = messagesContainerRef.current
    if (container) {
      container.scrollTop = container.scrollHeight
    }
  }, [messages, isLoading])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', handleKeyDown)
      return () => {
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
    return undefined
  }, [handleKeyDown])

  return (
    <>
      <div
        className="chat__backdrop"
        data-testid="chat-backdrop"
        onClick={onClose}
        role="button"
        tabIndex={-1}
        onKeyDown={(e) => e.key === 'Enter' && onClose()}
        aria-label={closeButtonLabel}
      />
      <div className="chat__panel" data-testid="chat-panel">
        <div className="chat__header">
          {logo ? <img src={logo} alt="Logo" className="chat__logo" /> : <div className='chat__logo--missing'/> }
          <button
            type="button"
            className="chat__close-button"
            onClick={onClose}
            aria-label={closeButtonLabel}
            data-testid="chat-close-button"
          >
            <FaIcon icon={faTimes} cssStyle={{ fontSize: 20 }} />
          </button>
        </div>
        <div className="chat__messages" ref={messagesContainerRef}>
          {welcomeMessage && (
            <ChatWelcomeMessage
              label={welcomeMessage.label}
              notMeButtonLabel={welcomeMessage.notMeButtonLabel}
              showButton={welcomeMessage.showButton}
              onNotMe={welcomeMessage.onNotMe}
            />
          )}
          {messages.filter((msg) => msg.text).map((msg) => (
            <ChatMessage key={msg.id} type={msg.type} text={msg.text} />
          ))}
          {isLoading && <ThinkingIndicator />}
        </div>
        {conversationEnded ? (
          <ChatRating
            onSubmit={onRatingSubmit}
            texts={ratingTexts}
            submitted={ratingSubmitted}
          />
        ) : (
          <ChatInput
            onSend={onSend}
            disabled={isLoading}
            placeholder={placeholder}
          />
        )}
      </div>
    </>
  )
}
